import { ArrowRight, CheckCheck, Flag, Scissors, Sparkles } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { type InsightSpan, type Transcript } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate, formatElapsed, formatTimecode } from "@/lib/format";
import { speakerColour, speakerName } from "@/lib/speakerColour";
import { SplitMeetingForm } from "@/pages/recording/SplitMeetingForm";

/**
 * A gap of this length between two turns reads as "somebody stopped and a different
 * conversation started" rather than a mid-meeting pause — short of a coffee-break, long
 * of the usual few-second beat between turns. Silence alone: it says nothing about
 * whether the same people came back, which is why it is a suggestion at the existing
 * "Split here" control rather than a second, automatic one.
 */
const SUGGESTED_SPLIT_GAP_MS = 3 * 60 * 1000;

export function TranscriptTab({
  transcript,
  recordingId,
  highlight,
  splittable,
}: {
  transcript: Transcript | null;
  recordingId: string;
  highlight?: InsightSpan[] | null;
  /** Whether "Split here" appears on each turn — only on a meeting whose content phase
   * has not run (recording/meeting split). Once it has, the meeting's summary and insights
   * were read off this exact span, and a split offered next to every turn would be noise
   * for a thing almost nobody wants to do then. */
  splittable?: boolean;
}) {
  const navigate = useNavigate();
  const { api } = useApp();

  // The segment a "Split here" click opened a form under, or null when none is open.
  const [splitAt, setSplitAt] = useState<number | null>(null);

  // Position, not row order — the same numbering the Speakers tab uses, so a colour
  // means one person across both.
  const order = useMemo(() => indexById(transcript), [transcript]);

  // UI-7 — the ranges to highlight, per segment. A segment can hold more than one (two
  // spans of one insight landing in the same turn), so it is a list.
  const ranges = useMemo(() => {
    const map = new Map<string, { start: number | null; end: number | null }[]>();
    for (const span of highlight ?? []) {
      const list = map.get(span.sourceSegmentId) ?? [];
      list.push({ start: span.startOffset, end: span.endOffset });
      map.set(span.sourceSegmentId, list);
    }
    return map;
  }, [highlight]);

  // A candidate split point per silence gap (#1 of the alternatives considered): the
  // segments are already in sequence order, so a gap is just consecutive turns whose
  // audio timestamps don't touch.
  const suggestedGapMs = useMemo(() => {
    const map = new Map<string, number>();
    const segments = transcript?.segments ?? [];
    for (let i = 1; i < segments.length; i++) {
      const previous = segments[i - 1];
      const segment = segments[i];
      if (!previous || !segment) continue;
      if (previous.endsAtMs === null || segment.startsAtMs === null) continue;

      const gap = segment.startsAtMs - previous.endsAtMs;
      if (gap >= SUGGESTED_SPLIT_GAP_MS) map.set(segment.id, gap);
    }
    return map;
  }, [transcript]);

  // The first spanned segment, in reading order, is the one to bring into view.
  const firstHit = useMemo(
    () => transcript?.segments.find((segment) => ranges.has(segment.id))?.id ?? null,
    [transcript, ranges],
  );
  const firstHitRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (firstHit) firstHitRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [firstHit]);

  if (!transcript || transcript.segments.length === 0) {
    return (
      <p className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
        The pipeline finished without producing a transcript. Turn developer mode on in Settings →
        Advanced and the Debug tab shows what each step was given and what it produced.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--space-6)]">
      <ReviewCard
        recordingId={recordingId}
        flaggedCount={transcript.flaggedCount}
        reviewedAt={transcript.reviewedAt}
        splittable={splittable ?? false}
      />

      <ol className="flex flex-col">
        {transcript.segments.map((segment, position) => {
          const index = segment.speakerId ? (order.get(segment.speakerId) ?? 0) : 0;
          const speaker = transcript.speakers.find((candidate) => candidate.id === segment.speakerId);
          const spanned = ranges.get(segment.id);

          return (
            <li
              key={segment.id}
              ref={segment.id === firstHit ? firstHitRef : undefined}
              className="group flex flex-col border-b border-[var(--border-subtle)] py-[var(--space-4)] last:border-b-0"
            >
              <div className="flex gap-[var(--space-6)]">
                <span className="w-[46px] shrink-0 pt-0.5 font-mono text-[length:var(--text-xs)] text-[var(--text-muted)]">
                  {formatTimecode(segment.startsAtMs)}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p
                      className="text-[length:var(--text-sm)] font-semibold underline decoration-1 underline-offset-4"
                      style={{ color: speaker ? speakerColour(index) : "var(--text-muted)" }}
                    >
                      {speaker ? speakerName(speaker, index) : "Unattributed"}
                    </p>
                    {segment.unverified && (
                      <span className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--warning)] px-2 py-px text-[length:var(--text-2xs)] text-[var(--warning)]">
                        <Sparkles size={10} strokeWidth={2} /> Unverified
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[length:var(--text-md)] leading-relaxed text-[var(--text-primary)]">
                    {spanned ? highlightText(segment.text, spanned) : segment.text}
                  </p>
                </div>

                {/* Splitting at the very first turn would leave nothing before it — the
                    same rule the Server enforces (split_meeting) — so the control is
                    absent there rather than offered and refused. */}
                {splittable && position > 0 && (
                  <button
                    type="button"
                    onClick={() => setSplitAt(splitAt === segment.sequence ? null : segment.sequence)}
                    title={
                      suggestedGapMs.has(segment.id)
                        ? `${formatElapsed(suggestedGapMs.get(segment.id))} of silence before this turn`
                        : undefined
                    }
                    className={cn(
                      "flex h-7 shrink-0 items-center gap-1.5 self-start rounded-[var(--radius-md)] border px-2 text-[length:var(--text-2xs)] font-medium transition-colors",
                      splitAt === segment.sequence
                        ? "border-[var(--primary)] text-[var(--primary)]"
                        : suggestedGapMs.has(segment.id)
                          ? "border-[var(--primary)] text-[var(--primary)]"
                          : "border-transparent text-[var(--text-muted)] opacity-0 group-hover:opacity-100 hover:border-[var(--border-default)] hover:text-[var(--text-secondary)]",
                    )}
                  >
                    <Scissors size={12} strokeWidth={2} />
                    {splitAt !== segment.sequence && suggestedGapMs.has(segment.id)
                      ? `Suggested split — ${formatElapsed(suggestedGapMs.get(segment.id))} gap`
                      : "Split here"}
                  </button>
                )}
              </div>

              {splitAt === segment.sequence && (
                <SplitMeetingForm
                  fromSequence={segment.sequence}
                  onCancel={() => setSplitAt(null)}
                  onSplit={async (input) => {
                    const created = await api.splitMeeting(recordingId, input);
                    navigate(`/recordings/${created.id}`);
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * UI-7 — a segment's effective text with an insight's spans marked in place.
 *
 * Null offsets mean the whole segment, and so does a range that no longer fits the text: a
 * correction applied since extraction can move the offsets (SPCH-8), and a mark that is a
 * little too wide still points the reader at the right line, where a silently mis-placed one
 * would not. The text is already the effective (corrected) text the tab renders, which is the
 * text extraction measured the offsets against, so the common case lands exactly.
 */
function highlightText(text: string, ranges: { start: number | null; end: number | null }[]): ReactNode {
  const mark = (content: string, key: string) => (
    <mark
      key={key}
      className="rounded-[var(--radius-sm)] bg-[color-mix(in_oklch,var(--vl-accent-warm)_28%,transparent)] px-0.5 text-[var(--text-primary)]"
    >
      {content}
    </mark>
  );

  const whole = ranges.some((r) => r.start === null || r.end === null);
  const clamped = whole
    ? []
    : ranges
        .map((r) => ({
          start: Math.max(0, Math.min(r.start ?? 0, text.length)),
          end: Math.max(0, Math.min(r.end ?? 0, text.length)),
        }))
        .filter((r) => r.end > r.start)
        .sort((a, b) => a.start - b.start);

  if (whole || clamped.length === 0) return mark(text, "whole");

  const parts: ReactNode[] = [];
  let cursor = 0;
  clamped.forEach((r, i) => {
    const start = Math.max(cursor, r.start);
    if (start > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, start)}</span>);
    if (r.end > start) parts.push(mark(text.slice(start, r.end), `m${i}`));
    cursor = Math.max(cursor, r.end);
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <>{parts}</>;
}

/**
 * The front door to Transcript Review (UI-12).
 *
 * The card is the design's own copy, and the reason it says what it says is that
 * word-level playback is the fastest way to find a bad transcription. What the owner
 * has already done to this transcript is shown here rather than only inside review,
 * because this is where they decide whether to open it at all.
 */
function ReviewCard({
  recordingId,
  flaggedCount,
  reviewedAt,
  splittable,
}: {
  recordingId: string;
  flaggedCount: number;
  reviewedAt: string | null;
  /** Whether Transcript Review should offer "Split here" once it opens — true only when
   * this card is reached from the recording/meeting-split flow, before the content phase
   * has run over this exact span (same rule the Transcript tab's own "Split here" uses). */
  splittable: boolean;
}) {
  return (
    <section className="flex flex-wrap items-center justify-between gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-[var(--space-6)] py-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="min-w-0">
        <p className="text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">
          Listen and check the transcript
        </p>
        <p className="mt-1 max-w-[70ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          The audio plays while a line moves word-by-word through the text — the fastest way to spot a bad
          transcription.
        </p>

        {(reviewedAt !== null || flaggedCount > 0) && (
          <div className="mt-2 flex flex-wrap items-center gap-[var(--space-3)]">
            {reviewedAt !== null && (
              <span className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--vl-life-soft)]">
                <CheckCheck size={13} strokeWidth={2.5} />
                Reviewed {formatDate(reviewedAt)}
              </span>
            )}
            {flaggedCount > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--warning)]">
                <Flag size={12} strokeWidth={2} />
                {flaggedCount} flagged
              </span>
            )}
          </div>
        )}
      </div>

      <Link
        to={`/recordings/${recordingId}/review`}
        state={{ splittable }}
        className="inline-flex h-9 shrink-0 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-4 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110"
      >
        Open review mode
        <ArrowRight size={14} strokeWidth={2.5} />
      </Link>
    </section>
  );
}

// ------------------------------------------------------------------ speakers

/** Speaker id → position, which is what decides their number and their colour. */
function indexById(transcript: Transcript | null): Map<string, number> {
  return new Map((transcript?.speakers ?? []).map((speaker, index) => [speaker.id, index]));
}
